import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  CheckIcon,
  CubeIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { productProfileApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import { PageHeader, StatusBadge, StatCard, LoadingSpinner } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

type ProductType =
  | 'CHECKING'
  | 'SAVINGS'
  | 'MONEY_MARKET'
  | 'CD'
  | 'MORTGAGE'
  | 'HELOC'
  | 'AUTO_LOAN'
  | 'PERSONAL_LOAN'
  | 'CREDIT_CARD'
  | 'COMMERCIAL_LOAN'
  | 'SBA_LOAN'
  | 'STUDENT_LOAN'
  | 'OTHER_PRODUCT';

interface RegulationMapping {
  id: string;
  regulationName: string;
  agency: string;
  cfrCitation: string;
  isRequired: boolean;
}

interface ChecklistItem {
  id: string;
  title: string;
  frequency: string;
  isCompleted: boolean;
  completedAt?: string;
  completedBy?: string;
  sortOrder: number;
}

interface ProductProfile {
  id: string;
  name: string;
  productType: ProductType;
  description?: string;
  launchDate?: string;
  regulatoryNotes?: string;
  isActive: boolean;
  regulationMappings: RegulationMapping[];
  checklist: ChecklistItem[];
}

interface DashboardData {
  total: number;
  active: number;
  regulationCoverage: number;
  incompleteChecklistItems: number;
  products: ProductProfile[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRODUCT_TYPES: { value: ProductType; label: string }[] = [
  { value: 'CHECKING', label: 'Checking' },
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'MONEY_MARKET', label: 'Money Market' },
  { value: 'CD', label: 'Certificate of Deposit' },
  { value: 'MORTGAGE', label: 'Mortgage' },
  { value: 'HELOC', label: 'HELOC' },
  { value: 'AUTO_LOAN', label: 'Auto Loan' },
  { value: 'PERSONAL_LOAN', label: 'Personal Loan' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'COMMERCIAL_LOAN', label: 'Commercial Loan' },
  { value: 'SBA_LOAN', label: 'SBA Loan' },
  { value: 'STUDENT_LOAN', label: 'Student Loan' },
  { value: 'OTHER_PRODUCT', label: 'Other' },
];

const LENDING_TYPES: ProductType[] = [
  'MORTGAGE',
  'HELOC',
  'AUTO_LOAN',
  'PERSONAL_LOAN',
  'CREDIT_CARD',
  'COMMERCIAL_LOAN',
  'SBA_LOAN',
  'STUDENT_LOAN',
];

const DEPOSIT_TYPES: ProductType[] = ['CHECKING', 'SAVINGS', 'MONEY_MARKET', 'CD'];

type TabKey = 'all' | 'lending' | 'deposits' | 'other';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'lending', label: 'Lending' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'other', label: 'Other' },
];

const PRODUCT_TYPE_BADGE_MAP: Record<string, BadgeVariant> = {
  CHECKING: 'info',
  SAVINGS: 'info',
  MONEY_MARKET: 'info',
  CD: 'info',
  MORTGAGE: 'success',
  HELOC: 'success',
  AUTO_LOAN: 'success',
  PERSONAL_LOAN: 'success',
  CREDIT_CARD: 'warning',
  COMMERCIAL_LOAN: 'success',
  SBA_LOAN: 'success',
  STUDENT_LOAN: 'success',
  OTHER_PRODUCT: 'neutral',
};

const FREQUENCY_BADGE_MAP: Record<string, BadgeVariant> = {
  DAILY: 'danger',
  WEEKLY: 'warning',
  MONTHLY: 'info',
  QUARTERLY: 'info',
  ANNUALLY: 'neutral',
  ONE_TIME: 'draft',
};

const DEFAULT_FORM = {
  name: '',
  productType: 'CHECKING' as ProductType,
  description: '',
  launchDate: '',
  regulatoryNotes: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProductComplianceProfilesPage() {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();

  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductProfile | null>(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  // Inline add checklist item
  const [addingChecklistFor, setAddingChecklistFor] = useState<string | null>(null);
  const [newChecklistItem, setNewChecklistItem] = useState({ title: '', frequency: 'MONTHLY' });

  // ─── Queries ──────────────────────────────────────────────────────────────────

  const { data: dashboardData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['product-profiles-dashboard', teamId],
    queryFn: () => productProfileApi.dashboard(teamId!),
    enabled: !!teamId,
    retry: 1,
  });

  const dashboard: DashboardData | undefined = dashboardData?.data;
  const products = dashboard?.products || [];

  // ─── Filtered Products ────────────────────────────────────────────────────────

  const filteredProducts = products.filter((p) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'lending') return LENDING_TYPES.includes(p.productType);
    if (activeTab === 'deposits') return DEPOSIT_TYPES.includes(p.productType);
    return !LENDING_TYPES.includes(p.productType) && !DEPOSIT_TYPES.includes(p.productType);
  });

  // ─── Mutations ──────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: typeof DEFAULT_FORM) => productProfileApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      toast.success('Product created successfully');
      handleCloseModal();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to create product');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof DEFAULT_FORM }) =>
      productProfileApi.update(teamId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      toast.success('Product updated successfully');
      handleCloseModal();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to update product');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => productProfileApi.delete(teamId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      toast.success('Product deleted');
      setExpandedProductId(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to delete product');
    },
  });

  const autoMapMutation = useMutation({
    mutationFn: (id: string) => productProfileApi.autoMap(teamId!, id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      const count = response?.data?.mappedCount ?? 0;
      toast.success(`Auto-mapped ${count} regulation${count !== 1 ? 's' : ''}`);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to auto-map regulations');
    },
  });

  const unmapRegulationMutation = useMutation({
    mutationFn: ({ productId, mappingId }: { productId: string; mappingId: string }) =>
      productProfileApi.unmapRegulation(teamId!, productId, mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      toast.success('Regulation mapping removed');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to remove mapping');
    },
  });

  const generateChecklistMutation = useMutation({
    mutationFn: (id: string) => productProfileApi.generateChecklist(teamId!, id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      const count = response?.data?.generatedCount ?? 0;
      toast.success(`Generated ${count} checklist item${count !== 1 ? 's' : ''}`);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to generate checklist');
    },
  });

  const addChecklistItemMutation = useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: { title: string; frequency: string } }) =>
      productProfileApi.addChecklistItem(teamId!, productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      toast.success('Checklist item added');
      setAddingChecklistFor(null);
      setNewChecklistItem({ title: '', frequency: 'MONTHLY' });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to add checklist item');
    },
  });

  const updateChecklistItemMutation = useMutation({
    mutationFn: ({
      productId,
      itemId,
      data,
    }: {
      productId: string;
      itemId: string;
      data: { isCompleted: boolean };
    }) => productProfileApi.updateChecklistItem(teamId!, productId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to update checklist item');
    },
  });

  const deleteChecklistItemMutation = useMutation({
    mutationFn: ({ productId, itemId }: { productId: string; itemId: string }) =>
      productProfileApi.deleteChecklistItem(teamId!, productId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-profiles-dashboard'] });
      toast.success('Checklist item removed');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to remove checklist item');
    },
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setForm(DEFAULT_FORM);
    setShowModal(true);
  };

  const handleOpenEdit = (product: ProductProfile) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      productType: product.productType,
      description: product.description || '',
      launchDate: product.launchDate ? product.launchDate.slice(0, 10) : '',
      regulatoryNotes: product.regulatoryNotes || '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setForm(DEFAULT_FORM);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const handleDelete = (product: ProductProfile) => {
    if (window.confirm(`Delete "${product.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(product.id);
    }
  };

  const handleToggleExpand = (productId: string) => {
    setExpandedProductId(expandedProductId === productId ? null : productId);
  };

  const handleChecklistToggle = (productId: string, item: ChecklistItem) => {
    updateChecklistItemMutation.mutate({
      productId,
      itemId: item.id,
      data: { isCompleted: !item.isCompleted },
    });
  };

  const handleAddChecklistItem = (productId: string) => {
    if (!newChecklistItem.title.trim()) return;
    addChecklistItemMutation.mutate({ productId, data: newChecklistItem });
  };

  const getProductTypeLabel = (type: ProductType) =>
    PRODUCT_TYPES.find((pt) => pt.value === type)?.label || type;

  // ─── Computed Stats ───────────────────────────────────────────────────────────

  const completedChecklist = (product: ProductProfile) =>
    product.checklist.filter((c) => c.isCompleted).length;

  const totalChecklist = (product: ProductProfile) => product.checklist.length;

  const progressPercent = (product: ProductProfile) => {
    const total = totalChecklist(product);
    if (total === 0) return 0;
    return Math.round((completedChecklist(product) / total) * 100);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (!teamId) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    const status = (error as any)?.response?.status;
    const isForbidden = status === 403;
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-4 text-lg font-semibold text-navy-900">
          {isForbidden ? 'Upgrade required' : 'Unable to load product profiles'}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {isForbidden
            ? 'Product Compliance Profiles require a Professional or Enterprise plan. Contact sales to enable access.'
            : (error as any)?.response?.data?.error ||
              'We ran into a problem loading this page. Please try again.'}
        </p>
        {!isForbidden && (
          <button
            onClick={() => refetch()}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <FeatureGate feature="product_compliance_profiles">
      <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Product Compliance Profiles"
          description="Manage product-specific compliance profiles, regulation mappings, and checklists."
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <button
                onClick={handleOpenCreate}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                Add Product
              </button>
            </PermissionGate>
          }
        />

        {/* Stats Row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={CubeIcon}
            label="Total Products"
            value={dashboard?.total ?? 0}
          />
          <StatCard
            icon={ShieldCheckIcon}
            label="Active Products"
            value={dashboard?.active ?? 0}
          />
          <StatCard
            icon={ClipboardDocumentListIcon}
            label="Regulation Coverage %"
            value={`${dashboard?.regulationCoverage ?? 0}%`}
          />
          <StatCard
            icon={ExclamationTriangleIcon}
            label="Incomplete Checklist Items"
            value={dashboard?.incompleteChecklistItems ?? 0}
          />
        </div>

        {/* Tab Bar */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                  activeTab === tab.key
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Product Table */}
        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-12 flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-12 text-center">
            <CubeIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm font-medium text-gray-900">No products found</p>
            <p className="mt-1 text-sm text-gray-500">
              {activeTab === 'all'
                ? 'Create your first product compliance profile to get started.'
                : 'No products match the selected category.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Product Type
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Mapped Regulations
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Checklist Progress
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProducts.map((product) => (
                    <Fragment key={product.id}>
                      {/* Main Row */}
                      <tr
                        className="transition-colors hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleToggleExpand(product.id)}
                      >
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-2">
                            <ChevronDownIcon
                              className={clsx(
                                'h-4 w-4 text-gray-400 transition-transform duration-200 shrink-0',
                                expandedProductId === product.id && 'rotate-180',
                              )}
                            />
                            <span className="font-medium text-navy-900">{product.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <StatusBadge variant={PRODUCT_TYPE_BADGE_MAP[product.productType] || 'neutral'}>
                            {getProductTypeLabel(product.productType)}
                          </StatusBadge>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {product.regulationMappings.length}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 max-w-[120px]">
                              <div className="h-2 w-full rounded-full bg-gray-200">
                                <div
                                  className="h-2 rounded-full bg-green-500 transition-all duration-300"
                                  style={{ width: `${progressPercent(product)}%` }}
                                />
                              </div>
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {completedChecklist(product)}/{totalChecklist(product)} completed
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span
                            className={clsx(
                              'inline-block h-2.5 w-2.5 rounded-full',
                              product.isActive ? 'bg-green-500' : 'bg-gray-300',
                            )}
                            title={product.isActive ? 'Active' : 'Inactive'}
                          />
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleOpenEdit(product)}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors"
                              title="Edit"
                            >
                              <PencilSquareIcon className="h-3.5 w-3.5" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(product)}
                              disabled={deleteMutation.isPending}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                              title="Delete"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {expandedProductId === product.id && (
                        <tr>
                          <td colSpan={6} className="bg-gray-50 px-4 py-0">
                            <ExpandedProductDetail
                              product={product}
                              onAutoMap={() => autoMapMutation.mutate(product.id)}
                              isAutoMapping={autoMapMutation.isPending}
                              onUnmapRegulation={(mappingId) =>
                                unmapRegulationMutation.mutate({ productId: product.id, mappingId })
                              }
                              isUnmapping={unmapRegulationMutation.isPending}
                              onGenerateChecklist={() => generateChecklistMutation.mutate(product.id)}
                              isGenerating={generateChecklistMutation.isPending}
                              onChecklistToggle={(item) => handleChecklistToggle(product.id, item)}
                              isTogglingChecklist={updateChecklistItemMutation.isPending}
                              onDeleteChecklistItem={(itemId) =>
                                deleteChecklistItemMutation.mutate({ productId: product.id, itemId })
                              }
                              addingChecklist={addingChecklistFor === product.id}
                              onStartAddChecklist={() => {
                                setAddingChecklistFor(product.id);
                                setNewChecklistItem({ title: '', frequency: 'MONTHLY' });
                              }}
                              onCancelAddChecklist={() => setAddingChecklistFor(null)}
                              newChecklistItem={newChecklistItem}
                              onNewChecklistItemChange={setNewChecklistItem}
                              onSubmitChecklistItem={() => handleAddChecklistItem(product.id)}
                              isAddingChecklist={addChecklistItemMutation.isPending}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit Product Modal */}
        <Transition appear show={showModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={handleCloseModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      {editingProduct ? 'Edit Product' : 'Add Product'}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      {editingProduct
                        ? 'Update the product compliance profile details.'
                        : 'Create a new product compliance profile to track regulations and checklist items.'}
                    </p>

                    <div className="mt-5 space-y-4">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="e.g., 30-Year Fixed Mortgage"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Product Type (Listbox) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Product Type <span className="text-red-500">*</span>
                        </label>
                        <Listbox
                          value={form.productType}
                          onChange={(value) => setForm({ ...form, productType: value })}
                        >
                          <div className="relative mt-1">
                            <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500">
                              <span className="block truncate">
                                {getProductTypeLabel(form.productType)}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                                {PRODUCT_TYPES.map((pt) => (
                                  <Listbox.Option
                                    key={pt.value}
                                    value={pt.value}
                                    className={({ active }) =>
                                      clsx(
                                        'relative cursor-pointer select-none py-2 pl-10 pr-4',
                                        active ? 'bg-teal-50 text-teal-900' : 'text-gray-900',
                                      )
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span className={clsx('block truncate', selected && 'font-medium')}>
                                          {pt.label}
                                        </span>
                                        {selected && (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          rows={3}
                          placeholder="Brief description of this product..."
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Launch Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Launch Date</label>
                        <input
                          type="date"
                          value={form.launchDate}
                          onChange={(e) => setForm({ ...form, launchDate: e.target.value })}
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Regulatory Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Regulatory Notes
                        </label>
                        <textarea
                          value={form.regulatoryNotes}
                          onChange={(e) => setForm({ ...form, regulatoryNotes: e.target.value })}
                          rows={2}
                          placeholder="Any special regulatory considerations..."
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={handleCloseModal}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={
                          !form.name.trim() ||
                          createMutation.isPending ||
                          updateMutation.isPending
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                      >
                        {createMutation.isPending || updateMutation.isPending
                          ? 'Saving...'
                          : editingProduct
                            ? 'Update Product'
                            : 'Create Product'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}

// ─── Expanded Product Detail ──────────────────────────────────────────────────

function ExpandedProductDetail({
  product,
  onAutoMap,
  isAutoMapping,
  onUnmapRegulation,
  isUnmapping,
  onGenerateChecklist,
  isGenerating,
  onChecklistToggle,
  isTogglingChecklist,
  onDeleteChecklistItem,
  addingChecklist,
  onStartAddChecklist,
  onCancelAddChecklist,
  newChecklistItem,
  onNewChecklistItemChange,
  onSubmitChecklistItem,
  isAddingChecklist,
}: {
  product: ProductProfile;
  onAutoMap: () => void;
  isAutoMapping: boolean;
  onUnmapRegulation: (mappingId: string) => void;
  isUnmapping: boolean;
  onGenerateChecklist: () => void;
  isGenerating: boolean;
  onChecklistToggle: (item: ChecklistItem) => void;
  isTogglingChecklist: boolean;
  onDeleteChecklistItem: (itemId: string) => void;
  addingChecklist: boolean;
  onStartAddChecklist: () => void;
  onCancelAddChecklist: () => void;
  newChecklistItem: { title: string; frequency: string };
  onNewChecklistItemChange: (item: { title: string; frequency: string }) => void;
  onSubmitChecklistItem: () => void;
  isAddingChecklist: boolean;
}) {
  const mappings = product.regulationMappings || [];
  const checklist = [...(product.checklist || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const completedCount = checklist.filter((c) => c.isCompleted).length;
  const totalCount = checklist.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="py-4 space-y-6">
      {/* Regulation Mappings */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-navy-900">
              Regulation Mappings ({mappings.length})
            </span>
          </div>
          <button
            onClick={onAutoMap}
            disabled={isAutoMapping}
            className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50 transition-colors"
          >
            <SparklesIcon className="h-3.5 w-3.5" />
            {isAutoMapping ? 'Mapping...' : 'Auto-Map Regulations'}
          </button>
        </div>
        {mappings.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-gray-400">
              No regulations mapped yet. Use Auto-Map to get started.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Regulation
                  </th>
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Agency
                  </th>
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    CFR Citation
                  </th>
                  <th className="py-2 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Required?
                  </th>
                  <th className="py-2 px-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {mappings.map((mapping) => (
                  <tr key={mapping.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2 px-4 text-sm font-medium text-navy-900">
                      {mapping.regulationName}
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-600">{mapping.agency}</td>
                    <td className="py-2 px-4 text-sm text-gray-600 font-mono text-xs">
                      {mapping.cfrCitation}
                    </td>
                    <td className="py-2 px-4 text-sm">
                      <StatusBadge variant={mapping.isRequired ? 'danger' : 'neutral'}>
                        {mapping.isRequired ? 'Required' : 'Optional'}
                      </StatusBadge>
                    </td>
                    <td className="py-2 px-4 text-right">
                      <button
                        onClick={() => onUnmapRegulation(mapping.id)}
                        disabled={isUnmapping}
                        className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Checklist */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-navy-900">
              Checklist ({completedCount}/{totalCount})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onStartAddChecklist}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Item
            </button>
            <button
              onClick={onGenerateChecklist}
              disabled={isGenerating}
              className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50 transition-colors"
            >
              <SparklesIcon className="h-3.5 w-3.5" />
              {isGenerating ? 'Generating...' : 'Generate Checklist'}
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        {totalCount > 0 && (
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="h-2.5 w-full rounded-full bg-gray-200">
                  <div
                    className="h-2.5 rounded-full bg-green-500 transition-all duration-300"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                {completedCount}/{totalCount} completed ({pct}%)
              </span>
            </div>
          </div>
        )}

        {/* Inline Add Form */}
        {addingChecklist && (
          <div className="px-4 py-3 border-b border-gray-200 bg-teal-50/50">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newChecklistItem.title}
                  onChange={(e) =>
                    onNewChecklistItemChange({ ...newChecklistItem, title: e.target.value })
                  }
                  placeholder="e.g., Review TILA disclosures"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-teal-500 focus:ring-teal-500"
                  autoFocus
                />
              </div>
              <div className="w-36">
                <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  value={newChecklistItem.frequency}
                  onChange={(e) =>
                    onNewChecklistItemChange({ ...newChecklistItem, frequency: e.target.value })
                  }
                  className="block w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="ANNUALLY">Annually</option>
                  <option value="ONE_TIME">One-Time</option>
                </select>
              </div>
              <button
                onClick={onSubmitChecklistItem}
                disabled={!newChecklistItem.title.trim() || isAddingChecklist}
                className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
              >
                {isAddingChecklist ? 'Adding...' : 'Add'}
              </button>
              <button
                onClick={onCancelAddChecklist}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Checklist Items */}
        {checklist.length === 0 && !addingChecklist ? (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-gray-400">
              No checklist items yet. Use Generate Checklist or add items manually.
            </p>
          </div>
        ) : (
          <ol className="divide-y divide-gray-100">
            {checklist.map((item, index) => (
              <li key={item.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3 pt-0.5">
                  <span className="text-xs font-medium text-gray-400 tabular-nums w-5 text-right">
                    {index + 1}.
                  </span>
                  <input
                    type="checkbox"
                    checked={item.isCompleted}
                    onChange={() => onChecklistToggle(item)}
                    disabled={isTogglingChecklist}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        'text-sm',
                        item.isCompleted
                          ? 'text-gray-400 line-through'
                          : 'text-gray-900',
                      )}
                    >
                      {item.title}
                    </span>
                    <StatusBadge variant={FREQUENCY_BADGE_MAP[item.frequency] || 'neutral'}>
                      {item.frequency.replace('_', '-').toLowerCase()}
                    </StatusBadge>
                  </div>
                  {item.isCompleted && item.completedAt && (
                    <p className="mt-0.5 text-xs text-gray-400">
                      Completed {new Date(item.completedAt).toLocaleDateString()}
                      {item.completedBy && ` by ${item.completedBy}`}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => onDeleteChecklistItem(item.id)}
                  className="shrink-0 rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  title="Remove item"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
